import { Injectable, OnDestroy, inject } from '@angular/core';
import {
  Entries,
  IP,
  IpTypeLabel,
  TRIFOLD_FIELD_MAP,
  TRIFOLD_MAP_DEFAULTS,
  TrifoldDetailMap,
  TrifoldFieldMap,
  TrifoldFieldMapByIPType,
  TrifoldOrderMap,
} from '@model/ftf-api.model';
import { FirstToFileFeatureFlags } from '@model/widget.model';
import { Store, select } from '@ngrx/store';
import { AccountState, initialState } from '@store/reducers/account.reducer';
import { FeatureFlagState, initialFeatureFlagsState } from '@store/reducers/feature-flags.reducer';
import { selectAccountState } from '@store/selectors/account.selectors';
import { selectFeatureFlags } from '@store/selectors/feature-flags.selectors';
import { BehaviorSubject, combineLatest, map, Subject, takeUntil } from 'rxjs';

function ipTypeToFeatureFlagKey(ipType: IP.TrifTypeEnum): keyof FirstToFileFeatureFlags | null {
  switch (ipType) {
    case IP.TrifTypeEnum.Patent:
      return 'patent';
    case IP.TrifTypeEnum.Trademark:
      return 'trademark';
    case IP.TrifTypeEnum.Matter:
      return 'matter';
    default:
      return null;
  }
}

@Injectable({ providedIn: 'root' })
/**
 * Represents a service for managing user accounts.
 */
export class AccountService implements OnDestroy {
  private store = inject(Store);

  protected $ngDestroyed = new Subject<void>();

  private isAccountStateLoadedSubject$ = new BehaviorSubject<boolean>(false);
  private isFeatureFlagStateLoadedSubject$ = new BehaviorSubject<boolean>(false);

  /**
   * Observable that emits a boolean value indicating whether the account details are loaded.
   */
  public isAccountStateLoaded$ = this.isAccountStateLoadedSubject$.asObservable();
  public isFeatureFlagsLoaded$ = this.isFeatureFlagStateLoadedSubject$.asObservable();

  public isAccountDetailsLoaded$;

  private accountState: AccountState = initialState;
  private featureState: FeatureFlagState = initialFeatureFlagsState;

  trifoldMap!: TrifoldDetailMap;
  trifoldOrderMap!: TrifoldOrderMap;
  accessibleTrifolds!: { [key in IP.TrifTypeEnum]: IP.TrifoldEnum[] };
  uploadableTrifolds!: { [key in IP.TrifTypeEnum]: IP.TrifoldEnum[] };
  ipTypes!: IpTypeLabel<IP.TypeEnum>[];
  trifIpTypes!: IpTypeLabel<IP.TrifTypeEnum>[];

  /**
   *  Creates an instance of AccountService.
   *
   * @remarks
   * The constructor initializes the AccountService by subscribing to the account and feature flag states from the store.
   * It sets up observables to track whether the account details and feature flags are loaded, and initializes
   * the service state based on the retrieved data.
   */
  constructor() {
    this.isAccountDetailsLoaded$ = combineLatest([this.isAccountStateLoaded$, this.isFeatureFlagsLoaded$]).pipe(
      map(([isAccountLoaded, isFeatureFlagsLoaded]) => isAccountLoaded && isFeatureFlagsLoaded),
      takeUntil(this.$ngDestroyed)
    );

    this.store.pipe(select(selectFeatureFlags)).subscribe((state: FeatureFlagState) => {
      this.featureState = state;
      if (state.isLoaded) {
        this.initializeFromState();
        this.isFeatureFlagStateLoadedSubject$.next(true);
      }
    });
    this.store.pipe(select(selectAccountState)).subscribe((state: AccountState) => {
      this.accountState = state;
      if (state.isLoaded) {
        this.initializeFromState();
        this.isAccountStateLoadedSubject$.next(true);
      }
    });
    this.initializeFromState();
  }

  /**
   * Initializes the service state based on the current account and feature flag states.
   *
   * @remarks
   * This method sets up the trifold map, order map, accessible trifolds, uploadable trifolds,
   * and IP types based on the account details and feature flags. It processes roles, preferences,
   * and custom fields to determine access and permissions for various trifold sections.
   */
  private initializeFromState(): void {
    const { accountDetails } = this.accountState;

    const trifoldOrderMap: TrifoldOrderMap = {
      Patent: (accountDetails.patentTrifoldOrder?.split(/,/) as IP.TrifoldEnum[]) ?? ['L', 'M', 'R'],
      Trademark: (accountDetails.tmTrifoldOrder?.split(/,/) as IP.TrifoldEnum[]) ?? ['L', 'M', 'R'],
      Matter: (accountDetails.matterTrifoldOrder?.split(/,/) as IP.TrifoldEnum[]) ?? ['L', 'M', 'R'],
    };

    const ipTypes: IpTypeLabel<IP.TypeEnum>[] = [];
    const trifIpTypes: IpTypeLabel<IP.TrifTypeEnum>[] = [];
    if (accountDetails.roles?.includes('ROLE_CASE_ACCESS') && this.featureState.flags.patent) {
      ipTypes.push({ key: IP.TrifTypeEnum.Patent, value: 'Patent' });
    }
    if (accountDetails.roles?.includes('ROLE_TRADEMARK_ACCESS') && this.featureState.flags.trademark) {
      ipTypes.push({ key: IP.TrifTypeEnum.Trademark, value: 'Trademark' });
    }
    if (accountDetails.roles?.includes('ROLE_MATTER_ACCESS') && this.featureState.flags.matter) {
      ipTypes.push({ key: IP.TrifTypeEnum.Matter, value: 'Other Matter' });
    }
    if (accountDetails.roles?.includes('ROLE_REFERENCE_CREATE') && this.featureState.flags.patent) {
      ipTypes.push({ key: IP.TypeEnum.Reference, value: 'Reference Library' });
    }
    ipTypes
      .filter((i) => i.key !== 'Reference' && i.key !== 'CaseReference')
      .forEach((ipType) => trifIpTypes.push(ipType as IpTypeLabel<IP.TrifTypeEnum>));

    const trifoldMap: TrifoldDetailMap = JSON.parse(JSON.stringify(TRIFOLD_MAP_DEFAULTS));
    const accessibleTrifolds: { [key in IP.TrifTypeEnum]: IP.TrifoldEnum[] } = { Patent: [], Trademark: [], Matter: [] };
    const uploadableTrifolds: { [key in IP.TrifTypeEnum]: IP.TrifoldEnum[] } = { Patent: [], Trademark: [], Matter: [] };

    const labels = accountDetails.customFields ?? {};
    const prefs = accountDetails?.preferences ?? {};
    (Object.entries(TRIFOLD_FIELD_MAP) as Entries<TrifoldFieldMapByIPType>).forEach(([ipType, trifoldDef]) => {
      (Object.entries(trifoldDef) as Entries<TrifoldFieldMap>).forEach(([trifold, fieldDef]) => {
        const ffKey = ipTypeToFeatureFlagKey(ipType);
        const access =
          // role check
          ((fieldDef.permissions.access.every((perm) => accountDetails.roles?.includes(perm)) &&
            // preference check
            prefs[fieldDef.displayPreference] === 'Yes') ??
            false) &&
          // feature flag check
          (ffKey ? this.featureState.flags[ffKey] : true);

        trifoldMap[ipType][trifold as IP.TrifoldEnum] = {
          label: labels[fieldDef.field] ?? fieldDef.default,
          access: access,
          version: access && fieldDef.permissions.version.every((perm) => accountDetails.roles?.includes(perm)),
          upload:
            access && this.featureState.flags.upload && fieldDef.permissions.upload.every((perm) => accountDetails.roles?.includes(perm)),
          lock: access && fieldDef.permissions.lock.every((perm) => accountDetails.roles?.includes(perm)),
          deleteFile: access && fieldDef.permissions.deleteFile.every((perm) => accountDetails.roles?.includes(perm)),
        };
      });

      const trifoldOrder = trifoldOrderMap[ipType];
      accessibleTrifolds[ipType] = Object.keys(trifoldMap[ipType])
        .filter((trifold) => trifoldMap[ipType][trifold as IP.TrifoldEnum].access)
        .sort((a, b) => trifoldOrder.indexOf(a as IP.TrifoldEnum) - trifoldOrder.indexOf(b as IP.TrifoldEnum)) as IP.TrifoldEnum[];

      uploadableTrifolds[ipType] = Object.keys(trifoldMap[ipType])
        .filter((trifold) => trifoldMap[ipType][trifold as IP.TrifoldEnum].upload)
        .sort((a, b) => trifoldOrder.indexOf(a as IP.TrifoldEnum) - trifoldOrder.indexOf(b as IP.TrifoldEnum)) as IP.TrifoldEnum[];
    });

    this.trifoldMap = trifoldMap;
    this.trifoldOrderMap = trifoldOrderMap;
    this.accessibleTrifolds = accessibleTrifolds;
    this.uploadableTrifolds = uploadableTrifolds;
    this.ipTypes = ipTypes;
    this.trifIpTypes = trifIpTypes;
  }

  /**
   * Resets the account details loaded state.
   *
   * @remarks
   * This method sets the internal subject tracking whether the account details are loaded to false.
   * It can be used to force a reload of account details when necessary.
   */
  public resetAccountDetailsLoaded(): void {
    this.isAccountStateLoadedSubject$.next(false);
  }

  /**
   * Checks if the user has a specific role.
   * @param role The role to check.
   * @returns True if the user has the role, false otherwise.
   */
  hasRole(role: string): boolean {
    return this.accountState.accountDetails.roles?.includes(role) ?? false;
  }

  /**
   * Gets the feature flags for the current user.
   *
   * @public
   * @readonly
   * @type {FirstToFileFeatureFlags}
   */
  public get featureFlags(): FirstToFileFeatureFlags {
    return this.featureState.flags;
  }

  /**
   * Gets the account details of the current user.
   *
   * @return {AccountDetails}
   */
  public get accountDetails() {
    return this.accountState.accountDetails ?? initialState.accountDetails;
  }

  /**
   * Gets the first name of the current user.
   *
   * @return {string}
   */
  public get firstName() {
    return this.accountState.accountDetails.firstName ?? '';
  }

  /**
   * Gets the last name of the current user.
   *
   * @return {string}
   */
  public get lastName() {
    return this.accountState.accountDetails.lastName ?? '';
  }

  /**
   * Gets the email address of the current user.
   *
   * @return {string}
   */
  public get email() {
    return this.accountState.accountDetails.emailAddress ?? '';
  }

  /**
   * Gets the full name of the current user.
   *
   * @return {string}
   */
  public get fullName() {
    return `${this.firstName} ${this.lastName}`;
  }

  /**
   * Gets the user subject to export control status of the current user.
   *
   * @return {boolean}
   */
  public get userSubjectToExportControl() {
    return this.accountState.accountDetails.userSubjectToExportControl === 'Y';
  }

  /**
   * Gets the custom field labels of the current user.
   *
   * @return {AccountState}
   */
  public get customFieldLabels() {
    return this.accountState.accountDetails.customFields ?? {};
  }

  /**
   * Gets the preferences of the current user.
   *
   * @return {AccountState}
   */
  public get preferences() {
    return this.accountState.accountDetails.preferences ?? {};
  }

  /**
   * Gets the user name of the current user.
   *
   * @return {string}
   */
  public get userName() {
    return this.accountState.accountDetails.userName ?? '';
  }

  /**
   * Gets the custom field labels of the current user.
   *
   * @return {AccountState}
   */
  public get clientName() {
    return this.accountState.accountDetails.clientName ?? '';
  }
  /**
   * Cleans up resources when the service is destroyed.
   *
   */
  ngOnDestroy() {
    this.$ngDestroyed.next();
    this.$ngDestroyed.complete();
  }
}
